import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { APP_CONFIG } from '@config/index'

type FooterLink = {
    label: string
    href: string
    isText?: boolean
    external?: boolean
}

type FooterSection = {
    title: string
    links: FooterLink[]
}

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear()
    const [visitCount, setVisitCount] = useState<number | null>(null)

    useEffect(() => {
        let isMounted = true
        const sessionKey = 'gme:visit-counted'

        const fetchVisitCount = async (): Promise<void> => {
            try {
                const hasCountedVisit = window.sessionStorage.getItem(sessionKey) === '1'
                const method = hasCountedVisit ? 'GET' : 'POST'
                const response = await fetch(APP_CONFIG.site.visitCounterEndpoint, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })

                if (!response.ok) {
                    throw new Error(`Visit counter request failed with ${response.status}`)
                }

                const data: { total?: number } = await response.json()
                if (!hasCountedVisit) {
                    window.sessionStorage.setItem(sessionKey, '1')
                }

                if (isMounted && typeof data.total === 'number') {
                    setVisitCount(data.total)
                }
            } catch (error) {
                console.error('Error loading visit counter', error)
            }
        }

        void fetchVisitCount()

        return () => {
            isMounted = false
        }
    }, [])

    const footerSections: FooterSection[] = [
        {
            title: 'Grupo Médico Empresarial',
            links: [
                { label: 'Tu salud visual y dental en manos expertas', href: '#', isText: true }
            ]
        },
        {
            title: 'Enlaces Rápidos',
            links: [
                { label: 'Inicio', href: '#inicio' },
                { label: 'Servicios', href: '#servicios' },
                { label: 'Nosotros', href: '#nosotros' },
                { label: 'Contacto', href: '#contacto' }
            ]
        },
        {
            title: 'Servicios',
            links: [
                { label: 'Exámenes Visuales', href: '#servicios' },
                { label: 'Lentes y Monturas', href: '#servicios' },
                { label: 'Consulta Dental', href: '#servicios' },
                { label: 'Odontología Estética', href: '#servicios' }
            ]
        },
        {
            title: 'Síguenos',
            links: [
                { label: 'Facebook', href: APP_CONFIG.social.facebook, external: true },
                { label: 'WhatsApp', href: APP_CONFIG.social.whatsapp, external: true }
            ]
        }
    ]

    const handleLinkClick = (href: string, external?: boolean) => {
        if (external) {
            window.open(href, '_blank', 'noopener,noreferrer')
        } else {
            const element = document.querySelector(href)
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }

    return (
        <footer className="bg-gray-900 text-white">
            <div className="container-fluid">
                {/* Main Footer Content */}
                <div className="py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {footerSections.map((section, index) => (
                            <motion.div
                                key={index}
                                className="space-y-4"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-semibold text-white mb-4">
                                    {section.title}
                                </h3>
                                <ul className="space-y-2">
                                    {section.links.map((link, linkIndex) => (
                                        <li key={linkIndex}>
                                            {link.isText ? (
                                                <p className="text-gray-400 text-sm">
                                                    {link.label}
                                                </p>
                                            ) : (
                                                <button
                                                    onClick={() => handleLinkClick(link.href, link.external)}
                                                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm block"
                                                >
                                                    {link.label}
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Footer Bottom */}
                <div className="border-t border-gray-800 py-8">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <motion.p
                            className="text-gray-400 text-sm text-center md:text-left"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            © {currentYear} Grupo Médico Empresarial. Todos los derechos reservados.
                        </motion.p>

                        <motion.div
                            className="rounded-full border border-gray-700 bg-gray-800/80 px-4 py-2 text-sm text-gray-300"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            Visitas del sitio: <span className="font-semibold text-white">{visitCount?.toLocaleString('es-MX') ?? '...'}</span>
                        </motion.div>

                        <motion.div
                            className="flex space-x-6"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <button
                                className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                                onClick={() => handleLinkClick('#')}
                            >
                                Aviso de Privacidad
                            </button>
                            <button
                                className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                                onClick={() => handleLinkClick('#')}
                            >
                                Términos y Condiciones
                            </button>
                        </motion.div>
                    </div>
                </div>

                {/* Contact Info Bar */}
                <div className="bg-gray-800 py-4">
                    <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-8 text-sm text-gray-300">
                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <span>{APP_CONFIG.contact.address}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            <span>{APP_CONFIG.contact.phone}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span>{APP_CONFIG.contact.hours.weekdays}</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer